
import Vue from 'vue';
import {
  CustomDropdown,
  CustomDropdownItem,
} from '@/libs/proof-approval/proofVersions/CustomDropdown';
import { fontList } from '@/constants/fontList';
import { fontSizesList } from '@/constants/fontSizes';
import {
  TextAlignLeft16,
  TextAlignCenter16,
  TextAlignRight16,
  TextBold16,
  TextItalic16,
  TextFont16,
} from '@carbon/icons-vue';
import {
  CvIconButton,
  CvTextArea,
  CvButton,
  CvDropdown,
  CvDropdownItem,
  CvAccordion,
  CvAccordionItem,
  CvNumberInput,
} from '@carbon/vue';
import { PmsColor } from '@midocean/webapps-shared-domain';
import { mapState, mapActions } from 'pinia';
import { ColorDropdown } from '../ColorManagement/ColorDropdown';
import TextCreation24 from '@carbon/icons-vue/es/text--creation/24';
import { useStore as useColorManagementStore } from '@/stores/v2/colorsManagement';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { useStore as useMirrorStore } from '@/stores/v2/mirror';
import ActionsPanel from './ActionsPanel.vue';
import { defaultTextTemplateV2 } from '@/components/w2pPintura/templates';
import { v4 as uuidv4 } from 'uuid';
import { createStateSnapshot } from '@/utils/historyHelperV2';
import { useStore as useHistoryStore } from '@/stores/v2/history';
import { useStore as useGlobalStore } from '@/stores/v2/global';
import Divider from '../Divider/Divider.vue';
import { generateEventPayloadV2 } from '@/utils/generatePriceParamsEventPayload';
import AlignShapeControls from './shared/AlignShapeControls.vue';
import RotationShapeControls from './shared/RotationShapeControls.vue';
import { debounce } from 'throttle-debounce';
import { AnnotationShape } from '../../typings/common';

export default Vue.extend({
  name: 'TextPanelOptions',
  components: {
    CustomDropdown,
    CustomDropdownItem,
    ColorDropdown,
    ActionsPanel,
    TextBold16,
    TextItalic16,
    TextAlignLeft16,
    TextAlignCenter16,
    TextAlignRight16,
    TextFont16,
    CvIconButton,
    CvTextArea,
    TextCreation24,
    CvButton,
    CvDropdown,
    CvDropdownItem,
    CvAccordion,
    CvAccordionItem,
    CvNumberInput,
    Divider,
    AlignShapeControls,
    RotationShapeControls,
  },
  props: {
    addTextBox: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      attributes: {
        text: '',
        fontFamily: fontList[0],
        fontSizePT: 2,
        fontWeight: 'normal',
        fontStyle: 'normal',
        textAlign: 'left',
        colorName: 'Pure White',
        color: {} as PmsColor,
      },
      fontList: fontList,
      fontSizesList: fontSizesList,
      hover: false,
      TextCreation24,
      debouncedClick: null as ((param1: string, param2: any) => void) | null,
    };
  },
  computed: {
    ...mapState(useColorManagementStore, ['availablePmsColors']),
    ...mapState(useShapeStore, ['shapes', 'activeShapeId', 'shapeById']),
    ...mapState(useProductStore, [
      'selectedPrintPosition',
      'selectedVariant',
      'rotation',
      'activePosition',
      'isFullColor',
      'isAnyPositionAdded',
      'isSublimationTechnique',
    ]),
    selectedPmsColor(): PmsColor {
      return (
        this.availablePmsColors.find(
          (c) => c.name === this.attributes.colorName
        ) ||
        this.availablePmsColors.find(
          (c) => c.name === this.attributes.colorName
        ) ||
        ({} as PmsColor)
      );
    },
    isDuplicateDisabled(): boolean {
      const activeShape = this.shapes.find(
        (shape) => shape.id === this.activeShapeId
      );
      return (
        activeShape?.pngFileWithoutConverters === undefined ||
        activeShape?.pngFileWithoutConverters === null
      );
    },
    allPmsColors(): PmsColor[] {
      return this.availablePmsColors;
    },
    isEditText(): boolean {
      return this.shapes.find((shape) => shape.id === this.activeShapeId)
        ?.pngFile
        ? true
        : false;
    },
    activeShape(): AnnotationShape | undefined {
      return this.shapes.find((shape) => shape.id === this.activeShapeId);
    },
  },
  watch: {
    activeShapeId(newVal, oldVal) {
      const activeShape = this.shapes.find((shape) => shape.id === newVal);
      if (activeShape && activeShape.kind === 'Text') {
        let item =
          fontList.find((i) => i.label == activeShape.w2pText.fontFamily) ??
          fontList[0];
        this.attributes = {
          text: activeShape.w2pText.text,
          fontFamily: item,
          fontSizePT: activeShape.w2pText.fontSizePT,
          fontWeight: activeShape.w2pText.fontWeight,
          fontStyle: activeShape.w2pText.fontStyle,
          textAlign: activeShape.w2pText.textAlign,
          colorName: activeShape.w2pText.colorName,
          color: activeShape.w2pText.color,
        };
      }

      if (newVal === '') {
        this.$emit('action:reset-text-box');
      }
    },

    'activeShape.w2pText.fontSizePT': {
      handler(newFontSize) {
        if (
          newFontSize !== undefined &&
          newFontSize !== this.attributes.fontSizePT
        ) {
          this.attributes.fontSizePT = newFontSize;
        }
      },
      immediate: true,
      deep: true,
    },

    activeShape: {
      handler(newVal: any, oldVal: any) {
        if (newVal && newVal.kind === 'Text') {
          if (this.attributes.colorName !== newVal.w2pText.colorName) {
            this.attributes.colorName = newVal.w2pText.colorName;
            this.attributes.color = newVal.w2pText.color;
          }
        }
      },
      deep: true,
    },

    addTextBox() {
      if (this.addTextBox) {
        this.initializeTextBox();
      }
    },
  },

  created() {
    const activeShape = this.shapes.find(
      (shape) => shape.id === this.activeShapeId
    );
    if (activeShape && activeShape.kind === 'Text') {
      let item =
        fontList.find((i) => i.label == activeShape.w2pText.fontFamily) ??
        fontList[0];
      this.attributes = {
        text: activeShape.w2pText.text,
        fontFamily: item,
        fontSizePT: activeShape.w2pText.fontSizePT,
        fontWeight: activeShape.w2pText.fontWeight,
        fontStyle: activeShape.w2pText.fontStyle,
        textAlign: activeShape.w2pText.textAlign,
        colorName: activeShape.w2pText.colorName,
        color: activeShape.w2pText.color,
      };
    }

    // Define the throttled function to handle clicks
    this.debouncedClick = debounce(1000, (param1: string, param2: number) => {
      this.onUpdate(param1, param2);
    });
  },
  mounted() {
    if (this.addTextBox) {
      this.initializeTextBox();
    }
  },
  methods: {
    ...mapActions(useShapeStore, [
      'textToImage',
      'removeShape',
      'duplicateShape',
      'deselectShape',
      'updateShapePosition',
      'checkIsShapeInArea',
      'transformShape',
    ]),
    ...mapActions(useProductStore, [
      'getVariantLaserColor',
      'polygonDetails',
      'printPositionArea',
      'isUncoloredConverterUsed',
    ]),
    ...mapActions(useMirrorStore, ['removeMirrorShape', 'updateMirrorShape']),
    ...mapActions(useNotificationStore, ['runValidators']),
    ...mapActions(useHistoryStore, ['recordHistory']),
    ...mapActions(useGlobalStore, ['setIsLoaderActive']),

    onUpdateClick(param1: string, param2: number) {
      if (this.debouncedClick) {
        // Call the throttledClick method with the passed parameters
        this.debouncedClick(param1, param2);
      }
    },

    onUpdate(whatToUpdate: string, value: number) {
      let newValue = value;
      if (whatToUpdate === 'height' || whatToUpdate === 'width') {
        newValue = value < 1 || isNaN(value) ? 1 : value;
      }

      if (whatToUpdate === 'rotation' && isNaN(value)) {
        return;
      }

      this.transformShape(whatToUpdate, newValue);
      this.runValidators();

      const activeShape = this.shapeById(this.activeShapeId);
      if (this.isSublimationTechnique && activeShape && activeShape.isInArea) {
        this.applySublimationConverter(this.activeShapeId);
      }

      if (activeShape) {
        this.updateMirrorShape(activeShape);
      }

      const eventPayload = generateEventPayloadV2();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);
    },

    removeTextBox() {
      this.removeMirrorShape(this.activeShapeId);
      this.removeShape(this.activeShapeId);
      this.runValidators();

      const eventPayload = generateEventPayloadV2();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);

      this.$emit('action:reset-text-box');
    },

    async duplicateTextBox() {
      await this.duplicateShape(this.activeShapeId);
      this.runValidators();

      const eventPayload = generateEventPayloadV2();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);
    },

    initializeTextBox() {
      if (this.activeShapeId) {
        this.attributes = {
          text: '',
          fontFamily: fontList[0],
          fontSizePT: 2,
          fontWeight: 'normal',
          fontStyle: 'normal',
          textAlign: 'left',
          colorName: 'Pure Black',
          color: {} as PmsColor,
        };
      }
      const artworkSpaceConsumed = 95;
      const template = { ...defaultTextTemplateV2, kind: 'Text' };

      // if (this.isLaserConverter()) {
      //   const configObj = this.getVariantLaserColor(
      //     this.selectedPrintPosition.id,
      //     this.selectedPrintPosition.techniqueId || '',
      //     this.selectedVariant.sku
      //   );

      //   const { red: r, green: g, blue: b } = configObj.laserColor;
      //   const colorRGBString = `rgba(${r},${g},${b},${
      //     configObj.laserPercentage / 100
      //   })`;
      // }

      template.id = uuidv4();
      template.isSelected = true;
      // template.kind = 'Text';

      if (
        this.activePosition.printArea &&
        this.activePosition.printArea.shapeType === 'Rectangle'
      ) {
        const printPosArea = this.printPositionArea(
          this.activePosition.id,
          true
        );
        if (printPosArea && printPosArea.kind === 'Rectangle') {
          const { x, y, width, height, rotation } = printPosArea;
          template.width = (width * artworkSpaceConsumed) / 100;

          // make the text box taller and slightly higher inside the print area
          template.height = (height * (artworkSpaceConsumed - 20)) / 100;
          const freeVerticalSpace = height - template.height;
          template.x = x + width / 2 - template.width / 2;
          template.y = y + freeVerticalSpace * 0.25;
          template.rotation = this.rotation;
        }
      }

      if (
        this.activePosition.printArea &&
        this.activePosition.printArea.shapeType === 'Ellipse'
      ) {
        const printPosArea = this.printPositionArea(
          this.activePosition.id,
          true
        );
        if (printPosArea && printPosArea.kind === 'Ellipse') {
          const { x, y, rx, ry } = printPosArea;
          template.width = rx;
          template.height = ry;
          template.x = x - template.width / 2;
          template.y = y - template.height / 2;
          template.rotation = this.rotation;
        }
      }

      if (
        this.activePosition.printArea &&
        this.activePosition.printArea.shapeType === 'Polygon'
      ) {
        template.width =
          (this.polygonDetails(this.activePosition.id).minDistance *
            (artworkSpaceConsumed - 20)) /
          100;
        template.height =
          (this.polygonDetails(this.activePosition.id).minDistance *
            (artworkSpaceConsumed - 20)) /
          100;
        template.x =
          this.polygonDetails(this.activePosition.id).x - template.width / 2;
        template.y =
          this.polygonDetails(this.activePosition.id).y - template.height / 2;
        template.rotation = 0;
      }

      this.$emit('add-to:image-decoration', template);
    },

    updateFontFamilyAttribute(value: any) {
      this.attributes.fontFamily = value;
    },

    updateFontSizeAttribute(value: number) {
      this.attributes.fontSizePT = Math.round(value);
    },

    setFontWeightOrStyle(variant: string) {
      if (variant === 'bold') {
        this.attributes.fontWeight =
          this.attributes.fontWeight === 'bold' ? 'normal' : variant;
      } else {
        this.attributes.fontStyle =
          this.attributes.fontStyle === 'italic' ? 'normal' : variant;
      }
    },
    async insertText() {
      this.setIsLoaderActive(true);

      const color = this.availablePmsColors.find(
        (c) => c.name === this.attributes.colorName
      );

      if (color) {
        await this.textToImage(
          this.attributes.fontFamily.label,
          Number(this.attributes.fontSizePT),
          this.attributes.fontWeight,
          this.attributes.fontStyle,
          this.attributes.text,
          this.attributes.textAlign ?? '',
          this.attributes.colorName,
          '',
          color,
          true,
          this.activeShapeId
        );

        const activeShape = this.shapes.find(
          (shape) => shape.id === this.activeShapeId
        );
        const scaledArea =
          this.activePosition.id &&
          this.printPositionArea(this.activePosition.id, true);
        const productArea = this.activePosition.printArea;

        if (activeShape && scaledArea && productArea) {
          this.updateShapePosition(activeShape, scaledArea, productArea);
          this.checkIsShapeInArea(activeShape.id);
          this.updateMirrorShape(activeShape);
          this.runValidators();
        }

        const eventPayload = generateEventPayloadV2();

        this.$emit('wizard:price-params-changed', {
          productPrintPositions: eventPayload,
        });

        const snapshot = createStateSnapshot();
        this.recordHistory(snapshot);

        this.setIsLoaderActive(false);
      }
    },
  },
});
